// Part Intelligence for one RFQ line (Phase 1B).
//
// Read docs/part-intelligence/FEATURE_CHARTER.md before changing anything here.
//
// Shows three DIFFERENT facts and never merges them:
//   machine evidence    what FLIS / PartsBase returned, per source
//   machine assessment  Corroborated / Possible match / Unknown
//   human decision      what a broker recorded, by whom, when
//
// The customer's identifier is displayed exactly as entered and never edited
// here. Loading or expanding this panel makes NO PartsBase call: the data is
// stored evidence from GET /api/rfqs/:id/part-intelligence (one request per
// RFQ, shared by every line through the query cache). A PartsBase search stays
// an explicit action in the PartsBase listings panel, which refreshes this one.

import { useState, type ReactNode } from 'react';
import { useAuth } from '@clerk/clerk-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ChevronRight, Fingerprint } from 'lucide-react';
import { Link } from 'wouter';
import {
  getPartIntelligence,
  recordPartIdentityDecision,
  type LineStage,
  type PartIdentityDecisionInput,
  type PartIdentityDecisionRecord,
  type PartIntelligenceLine,
} from '@/lib/api';
import {
  IDENTIFIER_TYPE_LABEL,
  IDENTIFIER_TYPE_VALUES,
  IDENTITY_LABEL,
  MACHINE_STATE_LABEL,
  SEARCH_STATE_LABEL,
} from '@shared/part-intelligence/decisions';
import { AltPartFinder } from './alt-part-finder';
import { useCatalogFeedback } from '../lib/use-catalog-feedback';
import { useCopilot } from '../lib/copilot-context';
import { CandidateList } from './copilot/proposal-cards';

export const partIntelligenceKey = (rfqPublicId: string) => ['part-intelligence', rfqPublicId] as const;

const MACHINE_PILL: Record<string, string> = {
  corroborated: 'pill pill-primary',
  possible_match: 'pill pill-warning',
  unknown: 'pill pill-default',
};
const SOURCE_PILL: Record<string, string> = {
  hit: 'pill pill-primary',
  no_hit: 'pill pill-default',
  not_searched: 'pill pill-default',
  error: 'pill pill-danger',
};

function fmtDate(iso: string | null | undefined): string {
  if (!iso) return '';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
}

function decisionText(r: PartIdentityDecisionRecord): string {
  if (r.kind === 'identity') {
    const label = IDENTITY_LABEL[r.value as keyof typeof IDENTITY_LABEL] ?? r.value;
    return r.candidateIdentifier ? `${label}: ${r.candidateIdentifier}` : label;
  }
  if (r.kind === 'catalog_issue') return r.value === 'suspected' ? 'Suspected catalog issue' : 'Catalog issue withdrawn';
  return `Type: ${IDENTIFIER_TYPE_LABEL[r.value as keyof typeof IDENTIFIER_TYPE_LABEL] ?? r.value}`;
}

interface Props {
  rfqPublicId: string;
  lineItemPublicId: string;
  partNumber: string;
  stage: LineStage;
}

export function PartIntelligencePanel({ rfqPublicId, lineItemPublicId, partNumber, stage }: Props) {
  const { getToken } = useAuth();
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const catalogFeedback = useCatalogFeedback();

  // One request per RFQ; every line's panel shares it through this key.
  const { data, isLoading, isError, error } = useQuery({
    queryKey: partIntelligenceKey(rfqPublicId),
    queryFn: () => getPartIntelligence(rfqPublicId, () => getToken()),
    staleTime: 60 * 1000,
    retry: false,
  });

  const save = useMutation({
    mutationFn: (input: PartIdentityDecisionInput) =>
      recordPartIdentityDecision(rfqPublicId, lineItemPublicId, input, () => getToken()),
    onSuccess: () => qc.invalidateQueries({ queryKey: partIntelligenceKey(rfqPublicId) }),
  });

  const line = data?.lines.find((l) => l.lineItemId === lineItemPublicId);
  const status = (error as { status?: number } | null)?.status;
  // A viewer is not allowed to read this; show nothing rather than an error.
  if (isError && status === 403) return null;

  return (
    <div className="mt-3 border border-border rounded-md bg-surface-muted/40 overflow-hidden">
      <button
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className="w-full flex items-center justify-between gap-2 px-3 py-2 text-xs hover:bg-surface-muted/70 transition-colors text-left"
      >
        <span className="flex items-center gap-1.5 flex-wrap min-w-0">
          <Fingerprint className="w-3.5 h-3.5 text-primary-strong shrink-0" />
          <span className="font-medium text-primary-strong">Part identity</span>
          {isLoading && <span className="text-foreground-muted">loading…</span>}
          {isError && <span className="text-destructive">unavailable</span>}
          {line && <SummaryPills line={line} />}
        </span>
        <ChevronRight className={`w-3.5 h-3.5 text-foreground-muted shrink-0 transition-transform ${open ? 'rotate-90' : ''}`} />
      </button>
      {line && (
        <div className="px-3 pb-2 -mt-1 text-[11px] text-foreground-muted">{line.nextAction}</div>
      )}

      {open && line && (
        <div className="px-3 pb-3 pt-1 text-xs space-y-3 border-t border-border">
          <IdentifierBlock line={line} />
          <EvidenceBlock line={line} />
          <DecisionBlock
            line={line}
            catalogSource={data?.catalogSource ?? null}
            catalogFeedback={catalogFeedback.canFlag}
            saving={save.isPending}
            error={save.error ? ((save.error as { message?: string }).message ?? 'Could not save.') : null}
            onSave={(input) => save.mutateAsync(input)}
          />
          <ResearchWithCopilot lineItemPublicId={lineItemPublicId} />
          <CopilotCandidates partNumber={partNumber} />
          {/* Same-NSN cross-references: the existing finder, unchanged behavior. */}
          <AltPartFinder rfqPublicId={rfqPublicId} lineItemPublicId={lineItemPublicId} partNumber={partNumber} stage={stage} embedded />
        </div>
      )}
    </div>
  );
}

export function SummaryPills({ line }: { line: PartIntelligenceLine }) {
  const { machine, sources } = line.snapshot;
  const human = line.decisions.identity;
  return (
    <>
      <span className={MACHINE_PILL[machine.state]} title="Machine assessment from FLIS and PartsBase evidence">
        {MACHINE_STATE_LABEL[machine.state]}
      </span>
      {human && (
        <span className={human.value === 'confirmed_match' ? 'pill pill-success' : 'pill pill-info'} title={`Recorded by ${human.decidedBy ?? 'a broker'}`}>
          {human.value === 'confirmed_match' ? 'Broker confirmed' : 'Needs research'}
        </span>
      )}
      {line.decisions.catalogIssue && <span className="pill pill-warning">Catalog issue flagged</span>}
      <span className={SOURCE_PILL[sources.flis.state]}>FLIS · {SEARCH_STATE_LABEL[sources.flis.state]}</span>
      <span className={SOURCE_PILL[sources.partsbase.state]}>
        PartsBase · {SEARCH_STATE_LABEL[sources.partsbase.state]}
        {sources.partsbase.basis === 'loose_key' ? ' (spelling unverified)' : ''}
      </span>
    </>
  );
}

function Row({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-[9rem_1fr] gap-x-3 gap-y-0.5">
      <div className="text-foreground-muted">{label}</div>
      <div className="min-w-0 break-words">{children}</div>
    </div>
  );
}

export function IdentifierBlock({ line }: { line: PartIntelligenceLine }) {
  const { identifier, sources } = line.snapshot;
  // Differently punctuated FLIS references, one entry per spelling.
  const seen = new Set<string>();
  const possible = sources.flis.looseCandidates.filter((c) => (seen.has(c.partNumber) ? false : (seen.add(c.partNumber), true)));
  return (
    <section className="space-y-1.5 pt-2">
      <Row label="Customer entered">
        <span className="font-mono text-foreground whitespace-pre-wrap">{identifier.original}</span>
      </Row>
      <Row label="Lookup key">
        <span className="font-mono">{identifier.normalized || '—'}</span>
        <span className="text-foreground-muted"> · formatting only, not proof of identity</span>
      </Row>
      <Row label="Possible match">
        {possible.length === 0 ? (
          <span className="text-foreground-muted">None found</span>
        ) : (
          <span className="flex flex-wrap gap-x-3 gap-y-0.5">
            {possible.map((c) => (
              <span key={c.partNumber}>
                <span className="font-mono text-foreground">{c.partNumber}</span>
                {c.itemName && <span className="text-foreground-muted"> ({c.itemName})</span>}
              </span>
            ))}
            <span className="text-foreground-muted">· differently punctuated FLIS reference, a candidate only</span>
          </span>
        )}
      </Row>
    </section>
  );
}

export function EvidenceBlock({ line }: { line: PartIntelligenceLine }) {
  const { flis, partsbase } = line.snapshot.sources;
  const first = flis.exactMatches[0];
  return (
    <section className="space-y-1.5">
      <div className="text-[11px] uppercase tracking-wide text-foreground-muted">Evidence</div>
      <Row label="DLA FLIS">
        <span className={SOURCE_PILL[flis.state]}>{SEARCH_STATE_LABEL[flis.state]}</span>
        {flis.state === 'hit' && first && (
          <span className="text-foreground-muted">
            {' '}{flis.exactMatches.length} exact reference{flis.exactMatches.length === 1 ? '' : 's'}
            {first.itemName ? ` · ${first.itemName}` : ''} · NSN {first.nsn}
          </span>
        )}
        <span className="text-foreground-muted">
          {flis.dataset?.pubDate ? ` · ${flis.dataset.pubDate} edition` : ' · no edition loaded'}
        </span>
      </Row>
      <Row label="PartsBase">
        <span className={SOURCE_PILL[partsbase.state]}>{SEARCH_STATE_LABEL[partsbase.state]}</span>
        <span className="text-foreground-muted">
          {partsbase.basis === 'exact' && ' · exact spelling'}
          {partsbase.basis === 'loose_key' && ' · cached under a punctuation-free key, so the searched spelling is unknown'}
          {partsbase.observedAt && ` · ${fmtDate(partsbase.observedAt)}`}
          {partsbase.state === 'not_searched' && ' · search from PartsBase listings when ready'}
        </span>
      </Row>
    </section>
  );
}

type Mode = null | 'confirm' | 'research' | 'catalog';

export function DecisionBlock({ line, catalogSource, catalogFeedback, saving, error, onSave }: {
  line: PartIntelligenceLine;
  catalogSource: string | null;
  /** The workspace manages catalog feedback (tenants.catalog_feedback_enabled,
   *  APD only today). When false the catalog-issue action and the review link
   *  are not rendered. Presentation only: the server refuses the write anyway. */
  catalogFeedback: boolean;
  saving: boolean;
  error: string | null;
  onSave: (input: PartIdentityDecisionInput) => Promise<unknown>;
}) {
  const [mode, setMode] = useState<Mode>(null);
  const [note, setNote] = useState('');
  const [choice, setChoice] = useState<string>('as_entered');
  const [other, setOther] = useState('');
  const [showHistory, setShowHistory] = useState(false);
  const { identity, catalogIssue, identifierType } = line.decisions;
  const { identifier, sources } = line.snapshot;
  const flisCandidates = [...new Set([
    ...sources.flis.exactMatches.map((m) => m.partNumber),
    ...sources.flis.looseCandidates.map((m) => m.partNumber),
  ])];

  const reset = () => { setMode(null); setNote(''); setChoice('as_entered'); setOther(''); };
  const submit = async (input: PartIdentityDecisionInput) => {
    try { await onSave({ ...input, ...(note.trim() ? { note: note.trim() } : {}) }); reset(); } catch { /* shown below */ }
  };
  const confirmInput = (): PartIdentityDecisionInput | null => {
    if (choice === 'as_entered') return { kind: 'identity', value: 'confirmed_match', candidateSource: 'as_entered', candidateIdentifier: identifier.normalized };
    if (choice === 'other') return other.trim() ? { kind: 'identity', value: 'confirmed_match', candidateSource: 'broker_entered', candidateIdentifier: other.trim() } : null;
    return { kind: 'identity', value: 'confirmed_match', candidateSource: 'flis_candidate', candidateIdentifier: choice };
  };

  const btn = 'px-2 py-1 border border-border rounded hover:bg-muted disabled:opacity-50';
  return (
    <section className="space-y-1.5">
      <div className="text-[11px] uppercase tracking-wide text-foreground-muted">Broker decision</div>
      <Row label="Identity">
        {identity ? (
          <span>
            <span className="font-medium text-foreground">{decisionText(identity)}</span>
            <span className="text-foreground-muted"> · {identity.decidedBy ?? 'a broker'}, {fmtDate(identity.decidedAt)}</span>
            {identity.note && <span className="block text-foreground-muted">“{identity.note}”</span>}
          </span>
        ) : <span className="text-foreground-muted">Not reviewed</span>}
      </Row>
      {catalogIssue && (
        <Row label="Catalog">
          <span className="font-medium text-foreground">Suspected catalog issue</span>
          <span className="text-foreground-muted"> · {catalogIssue.decidedBy ?? 'a broker'}, {fmtDate(catalogIssue.decidedAt)}
            {catalogIssue.catalogSourcePath ? ` · ${catalogIssue.catalogSourcePath}` : ''}</span>
          {catalogIssue.note && <span className="block text-foreground-muted">“{catalogIssue.note}”</span>}
          {catalogFeedback && (
            <Link href={`/catalog-review?line=${line.lineItemId}`} className="block text-primary hover:underline">
              Open in catalog review
            </Link>
          )}
        </Row>
      )}
      <Row label="Identifier type">
        <select
          aria-label="Identifier type (optional)"
          className="text-xs border border-border rounded px-1.5 py-0.5 bg-background max-w-full"
          disabled={saving}
          value={identifierType?.value ?? ''}
          onChange={(e) => void submit({ kind: 'identifier_type', value: e.target.value || 'cleared' })}
        >
          <option value="">Not labelled (optional)</option>
          {IDENTIFIER_TYPE_VALUES.filter((v) => v !== 'cleared').map((v) => (
            <option key={v} value={v}>{IDENTIFIER_TYPE_LABEL[v]}</option>
          ))}
        </select>
      </Row>

      {mode === null && (
        <div className="flex flex-wrap gap-1.5 pt-1">
          <button className={btn} disabled={saving} onClick={() => setMode('confirm')}>Confirm match…</button>
          <button className={btn} disabled={saving} onClick={() => setMode('research')}>Needs research…</button>
          {catalogFeedback && (catalogIssue
            ? <button className={btn} disabled={saving} onClick={() => void submit({ kind: 'catalog_issue', value: 'cleared' })}>Withdraw catalog issue</button>
            : <button className={btn} disabled={saving} onClick={() => setMode('catalog')}>Suspected catalog issue…</button>)}
          {identity && (
            <button className={`${btn} text-foreground-muted`} disabled={saving} onClick={() => void submit({ kind: 'identity', value: 'cleared' })}>
              Clear identity decision
            </button>
          )}
        </div>
      )}

      {mode === 'confirm' && (
        <div className="border border-border rounded p-2 space-y-1.5 bg-background">
          <div className="text-foreground-muted">Which identifier is the requested part? The customer’s entry stays as entered.</div>
          <label className="flex items-center gap-2">
            <input type="radio" name={`c-${line.lineItemId}`} checked={choice === 'as_entered'} onChange={() => setChoice('as_entered')} />
            <span>As entered: <span className="font-mono">{identifier.normalized}</span></span>
          </label>
          {flisCandidates.filter((c) => c !== identifier.normalized).map((c) => (
            <label key={c} className="flex items-center gap-2">
              <input type="radio" name={`c-${line.lineItemId}`} checked={choice === c} onChange={() => setChoice(c)} />
              <span>FLIS reference: <span className="font-mono">{c}</span></span>
            </label>
          ))}
          <label className="flex items-center gap-2 flex-wrap">
            <input type="radio" name={`c-${line.lineItemId}`} checked={choice === 'other'} onChange={() => setChoice('other')} />
            <span>Other:</span>
            <input className="font-mono border border-border rounded px-1.5 py-0.5 min-w-0 flex-1" maxLength={100}
              value={other} onFocus={() => setChoice('other')} onChange={(e) => setOther(e.target.value)} placeholder="Identifier from your research" />
          </label>
          <NoteField note={note} setNote={setNote} />
          <div className="flex gap-1.5">
            <button className={`${btn} font-medium`} disabled={saving || !confirmInput()} onClick={() => { const i = confirmInput(); if (i) void submit(i); }}>Save confirmation</button>
            <button className={btn} onClick={reset}>Cancel</button>
          </div>
        </div>
      )}

      {(mode === 'research' || mode === 'catalog') && (
        <div className="border border-border rounded p-2 space-y-1.5 bg-background">
          <div className="text-foreground-muted">
            {mode === 'research'
              ? 'Records that this identifier needs deeper research. Nothing is sent and no research starts automatically.'
              : `Records a review signal only. The catalog is not changed.${catalogSource ? ` Source page: ${catalogSource}` : ' No catalog source page was recorded for this RFQ.'}`}
          </div>
          <NoteField note={note} setNote={setNote} />
          <div className="flex gap-1.5">
            <button className={`${btn} font-medium`} disabled={saving}
              onClick={() => void submit(mode === 'research' ? { kind: 'identity', value: 'needs_research' } : { kind: 'catalog_issue', value: 'suspected' })}>
              Save
            </button>
            <button className={btn} onClick={reset}>Cancel</button>
          </div>
        </div>
      )}

      {error && <div className="text-destructive">{error}</div>}

      {line.history.length > 0 && (
        <div>
          <button className="text-foreground-muted hover:text-foreground" onClick={() => setShowHistory((v) => !v)}>
            {showHistory ? 'Hide' : 'Show'} decision history ({line.history.length})
          </button>
          {showHistory && (
            <ul className="mt-1 space-y-0.5">
              {line.history.map((r) => (
                <li key={r.id} className="text-foreground-muted">
                  {fmtDate(r.decidedAt)} · {r.decidedBy ?? 'a broker'} · <span className="text-foreground">{decisionText(r)}</span>
                  {r.machineStateAtDecision ? ` · machine said ${MACHINE_STATE_LABEL[r.machineStateAtDecision]}` : ''}
                  {r.note ? ` · “${r.note}”` : ''}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </section>
  );
}

function NoteField({ note, setNote }: { note: string; setNote: (v: string) => void }) {
  return (
    <textarea
      className="w-full text-xs border border-border rounded px-1.5 py-1 bg-background"
      rows={2}
      maxLength={1000}
      placeholder="Note (optional): what you checked, where you found it"
      value={note}
      onChange={(e) => setNote(e.target.value)}
    />
  );
}

/** Opens APD Copilot on this line in Research mode. Shown only where the
 *  workspace has the Copilot pilot; reads nothing and writes nothing itself. */
function ResearchWithCopilot({ lineItemPublicId }: { lineItemPublicId: string }) {
  const { capability, ask } = useCopilot();
  if (!capability.canUse) return null;
  return (
    <button
      className="px-2 py-1 border border-border rounded hover:bg-muted text-xs"
      onClick={() => ask({ scope: { type: 'rfq_line', ref: lineItemPublicId }, text: 'Research this part.', mode: 'research', autoSend: true })}
    >
      Research with Copilot
    </button>
  );
}

/** Research candidates recorded for this identifier (Copilot pilot only). */
function CopilotCandidates({ partNumber }: { partNumber: string }) {
  const { capability } = useCopilot();
  if (!capability.canUse) return null;
  return <CandidateList identifier={partNumber} />;
}
